import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type Method,
} from 'axios';
import type { BaseResponse, RequestConfig } from '../types/base';

// HTTP 方法类型
export type HttpMethod = Method;

// 请求选项
export interface RequestOptions extends RequestConfig {
  method?: HttpMethod;
  data?: unknown;
  params?: unknown;
  signal?: AbortSignal;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
}

export type CustomAxiosRequestConfig = AxiosRequestConfig & { autoErrorHandle?: boolean };

// 自定义响应类型，确保在响应拦截器中能够获取到自定义的请求配置
export type CustomAxiosResponse<T = unknown> = AxiosResponse<T> & {
  config: CustomAxiosRequestConfig;
};

// 兼容性工具函数 - 设置请求头
export const setRequestHeader = (
  config: InternalAxiosRequestConfig,
  key: string,
  value: string,
) => {
  if (!config.headers) {
    // 初始化 headers，避免使用 any 类型
    config.headers = {} as AxiosRequestHeaders;
  }

  // 兼容 Headers 对象和普通对象
  if (config.headers instanceof Headers) {
    config.headers.set(key, value);
  } else if (typeof config.headers === 'object') {
    config.headers[key] = value;
  }
};

// 创建哈希 - 使用无依赖的简单哈希算法
export const createHash = (data: string): string => {
  let hash = 0;
  const prime = 31;
  const mod = 1e9 + 7; // 大质数，避免溢出

  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (((hash * prime) % mod) + char) % mod;
  }

  // 转换为16进制字符串，确保长度为8位
  return hash.toString(16).padStart(8, '0');
};

// 错误类型
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: AxiosResponse,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromAxiosError(error: AxiosError): ApiError {
    return new ApiError(
      error.message,
      error.response?.status,
      error.response,
      error.response?.data,
    );
  }
}

// 纯函数重试器，便于测试与复用
const retryWithPolicy = async <T>(
  requestFn: () => Promise<AxiosResponse<BaseResponse<T>>>,
  policy?: RetryConfig,
  attempt: number = 0,
): Promise<BaseResponse<T>> => {
  try {
    const res = await requestFn();
    return res.data;
  } catch (e) {
    // 检查重试是否启用，默认禁用
    if (!policy || !policy.enabled || attempt >= policy.maxRetries) throw e;
    const apiErr = e as ApiError;
    const shouldRetry = policy.retryCondition
      ? policy.retryCondition(apiErr)
      : apiErr instanceof ApiError && (apiErr.status ?? 0) >= 500;
    if (!shouldRetry) throw e;
    await new Promise(resolve =>
      setTimeout(resolve, (policy.retryDelay ?? 1000) * Math.pow(2, attempt)),
    );
    return retryWithPolicy(requestFn, policy, attempt + 1);
  }
};

// 重试配置
export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  retryCondition?: (error: ApiError) => boolean;
  enabled?: boolean; // 是否启用重试，默认为 false
}

// API 客户端配置
export interface ApiClientConfig extends RequestConfig {
  retry?: RetryConfig;
  requestInterceptors?: ((
    config: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>)[];
  // 在响应拦截器中可通过 response.config.autoErrorHandle 读取自定义配置
  responseInterceptors?: ((
    response: CustomAxiosResponse,
  ) => CustomAxiosResponse | Promise<CustomAxiosResponse>)[];
  errorInterceptors?: (error: AxiosError) => AxiosError | Promise<AxiosError>;
}

// API 客户端类
export class ApiClient {
  private axiosInstance: AxiosInstance;
  private retryConfig?: RetryConfig;
  private requestInterceptorIds: number[] = [];
  private responseInterceptorIds: number[] = [];

  public hash: string = '';

  /** 获取 baseURL（供非 axios 场景使用，如 fetch / PostSSEClient） */
  get baseURL(): string {
    return (this.axiosInstance?.defaults?.baseURL as string) || '';
  }

  private initHash(config: ApiClientConfig) {
    this.hash = createHash(JSON.stringify(config));
  }

  // 创建 axios 实例，集中管理默认配置
  private createAxiosInstance(config: ApiClientConfig): AxiosInstance {
    return axios.create({
      baseURL: config.baseURL || '',
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });
  }

  // 统一注册请求/响应拦截器，保持构造函数简洁
  private registerInterceptors(instance: AxiosInstance, config: ApiClientConfig) {
    // 清理之前的拦截器 ID
    this.requestInterceptorIds = [];
    this.responseInterceptorIds = [];

    config.requestInterceptors?.forEach(interceptor => {
      const id = instance.interceptors.request.use(interceptor, config.errorInterceptors);
      this.requestInterceptorIds.push(id);
    });

    config.responseInterceptors?.forEach(interceptor => {
      const id = instance.interceptors.response.use(interceptor, config.errorInterceptors);
      this.responseInterceptorIds.push(id);
    });
  }

  constructor(config: ApiClientConfig = {}) {
    this.initHash(config);
    this.retryConfig = config.retry;

    // 创建 axios 实例并注册拦截器
    this.axiosInstance = this.createAxiosInstance(config);
    this.registerInterceptors(this.axiosInstance, config);
  }

  // 销毁方法：清理拦截器，避免内存泄露
  destroy(): void {
    // 清理请求拦截器
    this.requestInterceptorIds.forEach(id => {
      this.axiosInstance.interceptors.request.eject(id);
    });
    this.requestInterceptorIds = [];

    // 清理响应拦截器
    this.responseInterceptorIds.forEach(id => {
      this.axiosInstance.interceptors.response.eject(id);
    });
    this.responseInterceptorIds = [];

    // 清理引用
    // @ts-expect-error - 清理实例引用
    this.axiosInstance = null;
    // @ts-expect-error - 清理配置引用
    this.retryConfig = null;
  }

  // 重试逻辑改用纯函数
  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<BaseResponse<T>>>,
  ): Promise<BaseResponse<T>> {
    return retryWithPolicy(requestFn, this.retryConfig);
  }

  // 通用请求方法
  async request<T = unknown>(url: string, options: RequestOptions = {}): Promise<BaseResponse<T>> {
    const {
      method = 'GET',
      data,
      params,
      headers = {},
      timeout,
      signal,
      onUploadProgress,
      autoErrorHandle,
    } = options;

    // 构建 axios 请求配置
    const requestConfig: CustomAxiosRequestConfig = {
      url,
      method,
      data,
      params,
      headers,
      timeout,
      signal,
      autoErrorHandle,
      onUploadProgress: onUploadProgress
        ? progressEvent => {
            onUploadProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total || 0,
            });
          }
        : undefined,
    };

    const makeRequest = () => this.axiosInstance.request<BaseResponse<T>>(requestConfig);

    // 应用重试逻辑
    if (this.retryConfig) {
      return this.retryRequest(makeRequest);
    }

    try {
      const response = await makeRequest();
      return response.data;
    } catch (error) {
      throw error; // axios 拦截器已经处理了错误转换
    }
  }

  // GET 请求
  async get<T = unknown>(url: string, params?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { method: 'GET', params, ...config });
  }

  // POST 请求
  async post<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { method: 'POST', data, ...config });
  }

  // PUT 请求
  async put<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { method: 'PUT', data, ...config });
  }

  // DELETE 请求
  async delete<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { method: 'DELETE', data, ...config });
  }

  // PATCH 请求
  async patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig) {
    return this.request<T>(url, { method: 'PATCH', data, ...config });
  }

  // 并发请求
  async all<T extends Record<string, { url: string; options?: RequestOptions }>>(
    requests: T,
  ): Promise<{ [K in keyof T]: BaseResponse<unknown> }> {
    const entries = await Promise.allSettled(
      Object.entries(requests).map(async ([key, req]) => {
        const result = await this.request(req.url, req.options);
        return [key, result] as const;
      }),
    );

    return Object.fromEntries(
      entries.map((r, i) => {
        const key = Object.keys(requests)[i];
        if (r.status === 'fulfilled') return r.value;
        return [
          key,
          {
            data: null,
            error_code: -1,
            error_reason: r.reason instanceof Error ? r.reason.message : String(r.reason),
          } as BaseResponse<unknown>,
        ];
      }),
    ) as { [K in keyof T]: BaseResponse<unknown> };
  }
}

// 实例管理器
class ApiClientManager {
  private static instances = new Map<string, ApiClient>();
  private static instancePromises = new Map<string, Promise<ApiClient>>();

  static async getInstance(config: ApiClientConfig): Promise<ApiClient> {
    const hash = createHash(JSON.stringify(config));

    // 如果实例已存在且配置相同，直接返回
    if (this.instances.has(hash)) {
      return this.instances.get(hash)!;
    }

    // 如果正在创建中，等待创建完成
    if (this.instancePromises.has(hash)) {
      return this.instancePromises.get(hash)!;
    }

    // 创建新实例
    const instancePromise = Promise.resolve(new ApiClient(config));
    this.instancePromises.set(hash, instancePromise);

    try {
      const instance = await instancePromise;
      this.instances.set(hash, instance);
      this.instancePromises.delete(hash); // 清理 Promise
      return instance;
    } catch (error) {
      this.instancePromises.delete(hash);
      throw error;
    }
  }

  static async clearInstance(config?: ApiClientConfig): Promise<void> {
    if (config) {
      const hash = createHash(JSON.stringify(config));
      this.instances.delete(hash);
      this.instancePromises.delete(hash);
    } else {
      this.instances.clear();
      this.instancePromises.clear();
    }
  }

  static async hasInstance(config: ApiClientConfig): Promise<boolean> {
    const hash = createHash(JSON.stringify(config));
    return this.instances.has(hash);
  }

  static getInstanceCount(): number {
    return this.instances.size;
  }
}

export const createApiClient = (config: ApiClientConfig) => {
  // SSR 环境下每次创建新实例，不通过管理器缓存，避免跨请求共享
  if (import.meta.server) {
    return Promise.resolve(new ApiClient(config));
  }
  return ApiClientManager.getInstance(config);
};

export const useApiClient = async (config?: ApiClientConfig) => {
  if (!config) {
    throw new Error('Config is required for useApiClient');
  }
  return ApiClientManager.getInstance(config);
};

// 导出管理器方法供测试和调试使用
export const clearApiClient = (config?: ApiClientConfig) => {
  return ApiClientManager.clearInstance(config);
};

export const hasApiClient = (config: ApiClientConfig) => {
  return ApiClientManager.hasInstance(config);
};

export const getApiClientCount = () => {
  return ApiClientManager.getInstanceCount();
};
